import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { LocationDescriptor } from 'history';
import { MentalLinkIdentifier, PageLayout } from '../../types';
import MentalFooterMenu from '../MentalFooterMenu/MentalFooterMenu';
import PageLayoutComponent from '../PageLayout';
import { MentalPageProps } from './types';
import PageBack from '../PageBack';
import mentalApi from '../../lib/MentalApi';
import MentalNotFound from '../MentalNotFound';

export interface WrapperMatchParams {
    mentalId: string,
    mentalSlug: string,
}

interface Options<MatchParams, Data> {
    backLinkProvider?: (mentalLinkIdentifier: MentalLinkIdentifier) => LocationDescriptor,
    pageLayoutProvider: (mentalLinkIdentifier: MentalLinkIdentifier) => PageLayout,
    dataLoader: (
        mentalLinkIdentifier: MentalLinkIdentifier,
        matchParams: MatchParams,
    ) => Data | undefined,
}

/* eslint-disable react/prefer-stateless-function */

export default function withMentalPageElements<
    MatchParams extends { [K in keyof MatchParams]?: string } & WrapperMatchParams,
    P extends MentalPageProps<MatchParams, Data>,
    C,
    Data
>(
    WrappedContentComponent: React.JSXElementConstructor<P> & C,
    options: Options<MatchParams, Data>,
) {
    interface WrapperProps extends RouteComponentProps<MatchParams> {
    }

    type Props = JSX.LibraryManagedAttributes<C, WrapperProps
        & Exclude<C, MentalPageProps<MatchParams, Data>>>;

    const WrappedComponent: React.FC<Props> = ({ match, ...props }: Props) => {
        const mentalLinkIdentifier: MentalLinkIdentifier = {
            mentalId: match.params.mentalId,
            mentalSlug: match.params.mentalSlug,
        };

        const { backLinkProvider } = options;

        const data : Data | undefined = options.dataLoader(mentalLinkIdentifier, match.params);

        if (data === undefined) {
            return (
                <MentalNotFound
                    what="Mental"
                />
            );
        }

        const backLink = backLinkProvider ? backLinkProvider(mentalLinkIdentifier) : null;
        const footerMenuLayout = mentalApi.getMentalFooterMenuLayout(mentalLinkIdentifier.mentalId);
        const pageLayout = options.pageLayoutProvider(mentalLinkIdentifier);

        return (
            <>
                <PageLayoutComponent layout={pageLayout} />
                {backLink && <PageBack backlink={backLink} />}
                <WrappedContentComponent
                    mentalLinkIdentifier={mentalLinkIdentifier}
                    matchParams={match.params}
                    data={data}
                    {...props as any}
                />
                <MentalFooterMenu
                    mentalLinkIdentifier={mentalLinkIdentifier}
                    layout={footerMenuLayout}
                />
            </>
        );
    };

    return WrappedComponent;
}
